<script setup>

import {ref} from 'vue';
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import {Inertia} from "@inertiajs/inertia";
import PaginationLinks from "@/Components/PaginationLinks.vue";

const props = defineProps({
  companies: Object,
  pageSearchQuery: '',
});
const searchQuery = ref(props.pageSearchQuery);
const searching = ref(false);
const activating = ref(null);
const activateError = ref('');

const activate = (company) => {
  Inertia.post(route('admin.companies.activate', {company: company.ID}), {
    preserveScroll: true,
    onError: (error) => activateError.value = error.company_id
  });
  activating.value = null;
};

const makeSearchRequest = _.debounce(() => {
  searching.value = true;
  const queryParameters = route().params || {};
  if (queryParameters.page) {
    delete queryParameters.page;
  }
  queryParameters['query'] = searchQuery.value;
  Inertia.get(route(route().current(), queryParameters), {}, {preserveScroll: true});
  searching.value = false;
}, 400);

const startActivating = (company) => {
  activating.value = company;
}

const closeActivatedModalDialog = () => {
  activating.value = null;
}
</script>

<template>
  <AppLayout title="Inactive Companies">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Inactive Companies
      </h2>
    </template>

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="mt-5 mb-2 grid gap-2 lg:grid-cols-2 grid-cols-1">
          <div>
            <InputLabel for="search" value="Search" class="inline mr-2"/>
            <TextInput
              id="search"
              v-model="searchQuery"
              type="text"
              class="mt-1 inline"
              placeholder="John Doe"
              @keyup="makeSearchRequest"
              :disabled="searching"
            />
          </div>
          <div class="lg:text-right">
            <a :href="route('admin.companies.active')">
              <SecondaryButton>View Active Companies</SecondaryButton>
            </a>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light">
            <thead class="border-b font-medium dark:border-neutral-500">
            <tr>
              <th scope="col" class="px-4 py-2">Company Name</th>
              <th scope="col" class="px-4 py-2">Company Code</th>
              <th scope="col" class="px-4 py-2">Email</th>
              <th scope="col" class="px-4 py-2 hidden md:table-cell">Phone Number</th>
              <th scope="col" class="px-4 py-2">Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr
              class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
              v-for="company in companies.data">
              <td class="px-4 py-2 break-words" v-text="company.client"></td>
              <td class="px-4 py-2 break-words" v-text="company.client_code"></td>
              <td class="px-4 py-2 break-words" v-text="company.email"></td>
              <td class="px-4 py-2 break-words hidden md:table-cell" v-text="company.phone"></td>
              <td>
                <a :href="route('admin.companies.edit', {company: company.ID})" class="fa fa-pen mr-1 p-1"
                   :title="`Edit ${company.client}`">
                </a>
                <button class="fa fa-eye p-1" @click="startActivating(company)" :disabled="activating"
                        :title="`Activate ${company.client}`"
                ></button>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="text-center mt-5">
            <PaginationLinks :pagination="companies"/>
          </div>
        </div>
      </div>
    </div>
    <DialogModal :show="activating !== null" @close="closeActivatedModalDialog">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        You are about to activate <b v-text="activating.client"></b>.
        <br>Are you sure?
      </template>

      <template #footer>
        <SecondaryButton @click="closeActivatedModalDialog" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="activate(activating)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

  </AppLayout>
</template>
